<template>
  <div v-if="hasPermission('access_cases')">
    <b-card>
      <!-- Loading State -->
      <div v-if="load && !patient_case" class="text-center py-5">
        <b-spinner
          variant="primary"
          label="Loading..."
          style="width: 3rem; height: 3rem"
        ></b-spinner>
        <p class="mt-3 text-muted">Loading case details...</p>
      </div>

      <!-- Error State -->
      <b-alert v-if="error" show variant="danger" class="mb-3">
        <div class="d-flex align-items-center">
          <feather-icon icon="AlertCircleIcon" size="24" class="mr-2" />
          <div>
            <h5 class="mb-1 text-danger">Failed to load case details</h5>
            <p class="mb-0">{{ error }}</p>
          </div>
        </div>
        <div class="mt-3">
          <b-button variant="outline-danger" size="sm" @click="init">
            <feather-icon icon="RefreshCwIcon" class="mr-50" />
            Try Again
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="ml-1"
            :to="{ name: 'cases' }"
          >
            <feather-icon icon="ArrowLeftIcon" class="mr-50" />
            Back to Cases
          </b-button>
        </div>
      </b-alert>

      <show
        v-if="patient_case && !error"
        @refresh="init"
        :item="patient_case"
        :load="load"
        :isVertical="false"
        @changeTab="move"
      >
        <template #add_tab>
          <div class="row my-3">
            <div class="col d-flex justify-content-around">
              <b-badge
                v-if="patient_case && patient_case.auditor"
                pill
                :variant="`light-${getStatus(5).color}`"
                class="text-capitalize"
              >
                {{ patient_case.auditor.name }}
              </b-badge>

              <div v-if="patient_case.patient_type == 'OUTPATIENT'">
                <b-badge
                  v-if="patient_case && patient_case.patient_type"
                  pill
                  :variant="`light-${getStatus(2).color}`"
                  class="text-capitalize"
                >
                  Outpatient
                </b-badge>
              </div>

              <div v-if="patient_case.patient_type == 'INPATIENT'">
                <b-badge
                  v-if="patient_case && patient_case.patient_type"
                  pill
                  :variant="`light-${getStatus(3).color}`"
                  class="text-capitalize"
                >
                  Inpatient
                </b-badge>
              </div>
              <div>
                <b-badge
                  v-if="patient_case && patient_case.status"
                  pill
                  :variant="`light-${
                    getStatus(patient_case.status.status).color
                  }`"
                  class="text-capitalize"
                >
                  {{ patient_case.status.name }}
                </b-badge>
              </div>

              <div>
                <b-badge
                  v-if="patient_case && patient_case.assign_date"
                  pill
                  :variant="`light-${getStatus(6).color}`"
                  class="text-capitalize"
                >
                  Assign Date: {{ patient_case.assign_date }}
                </b-badge>
              </div>
            </div>
          </div>

          <b-tab
            title="Transaction History"
            :title-item-class="'tab-title-class'"
          >
            <template #title>
              <feather-icon icon="UserIcon" />
              <span>Medical Basic Information</span>
            </template>

            <list
              :data="patient_case"
              :loading="load"
              :only="[
                'account_no',
                'birth_date',
                'gender',
                'nationality',
                'patient_type',
                'financial_category_code',
                'financial_category',
                'financial_category_number',
                'medical_record',
                'ifa',
                'dr_name',
                'branch_name',
                'branch_code',
                'admission_date',
                'visit_no',
              ]"
            ></list>
            <validation-observer ref="medicalObserver">
              <b-row class="">
                <b-col md="8">
                  <strong class="ml-2">Charge Items</strong>
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="
                      getSectionError('charge_items') ||
                      getSectionError('charge_items_eye') ||
                      getSectionError('charge_items_dnt') ||
                      getSectionError('charge_items_operation')
                    "
                    :error="
                      getSectionError('charge_items') ||
                      getSectionError('charge_items_eye') ||
                      getSectionError('charge_items_dnt') ||
                      getSectionError('charge_items_operation')
                    "
                    title="Failed to load Charge Items"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    :items="patient_case.charge_items"
                    :fields="charge_items_fields"
                    striped
                  >
                    <template #cell(date)="data">
                      {{ data.item.date }}
                    </template>
                  </b-table>
                </b-col>
                <b-col md="4">
                  <b-row>
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="ChargeItemsStatus"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.charge_items_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>

                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <h6>Add Custom Comment</h6>
                      <b-form-textarea v-model="case_review.charge_items">
                      </b-form-textarea>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </validation-observer>
          </b-tab>

          <!-- branch name , code , chg item , surgery -->

          <b-tab>
            <template #title>
              <feather-icon icon="CheckSquareIcon" />
              <span>Audit View</span>
            </template>
            <validation-observer ref="auditViewObserver">
              <b-row class="mb-5 border py-2 card-shadow">
                <b-col md="8">
                  <h5 class="mb-1">Treatment Plan</h5>

                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('treatment_plan')"
                    :error="getSectionError('treatment_plan')"
                    title="Failed to load Treatment Plan"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    bordered
                    :items="patient_case.treatment_plan"
                    :fields="planfields"
                  ></b-table>
                </b-col>

                <b-col md="4">
                  <b-row>
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="treatmentPlanStatus"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.treatment_plan_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>

                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <h6>Add Custom Comment</h6>
                      <b-form-textarea v-model="case_review.treatment_plan">
                      </b-form-textarea>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row class="mb-5 border py-2 card-shadow">
                <!-- v-if="patient_case.medical_type == 1" -->
                <b-col md="8">
                  <!-- <h6 class="mb-1">Ifa Treatment plan</h6> -->
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('IfaTreatmentplan')"
                    :error="getSectionError('IfaTreatmentplan')"
                    title="Failed to load IFA Treatment Plan"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    class="text-lg-left"
                    :items="patient_case.IfaTreatmentplan"
                    :fields="IfaTreatmentplan_fields"
                    bordered
                  ></b-table>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="IfaTreatmentPlans"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.ifa_treatment_plan_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.IfaTreatmentPlans"
                        >
                        </b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row class="mb-5 border py-2 card-shadow">
                <!-- v-if="patient_case.medical_type == 1" -->
                <b-col md="8">
                  <h6 class="mb-1">Operative Procedures</h6>
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('operative_procedures')"
                    :error="getSectionError('operative_procedures')"
                    title="Failed to load Operative Procedures"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    class="text-lg-left"
                    :items="patient_case.operative_procedures"
                    :fields="operative_procedures_fields"
                    bordered
                  ></b-table>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="operativeProcedures"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.operative_procedure_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.operative_procedure"
                        >
                        </b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <b-row
                class="mb-5 border py-2 card-shadow"
                v-if="patient_case.medical_type == 1"
              >
                <b-col md="8">
                  <!-- <h6 class="mb-1">Operative Procedures</h6> -->
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('ended_operative_procedures')"
                    :error="getSectionError('ended_operative_procedures')"
                    title="Failed to load Ended Operative Procedures"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    class="text-lg-left"
                    :items="patient_case.ended_operative_procedures"
                    :fields="ended_operative_procedures_fields"
                    bordered
                  ></b-table>
                </b-col>
                <b-col md="1"> </b-col>
                <b-col md="3">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="operativeProcedures"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.ifa_operative_procedure_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.ifa_operative_procedure"
                        >
                        </b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row
                class="mb-5 border py-2 card-shadow"
                v-if="patient_case.medical_type == 1"
              >
                <!-- v-if="patient_case.medical_type == 1" -->
                <b-col md="8">
                  <!-- <h6 class="mb-1">ifa Operative Procedures</h6> -->
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('surgery')"
                    :error="getSectionError('surgery')"
                    title="Failed to load Surgery"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    class="text-lg-left"
                    :items="patient_case.surgery"
                    :fields="surgeries_fields"
                    bordered
                  ></b-table>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="operativeProcedures"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.ifa2_operative_procedure_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.ifa2_operative_procedure"
                        >
                        </b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row
                class="mb-5 border py-2 card-shadow"
                v-if="patient_case.medical_type == 1"
              >
                <!-- v-if="patient_case.medical_type == 1" -->
                <b-col md="8">
                  <h6 class="mb-1">Operative Order Details</h6>
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('OprOrderDetails')"
                    :error="getSectionError('OprOrderDetails')"
                    title="Failed to load Operative Order Details"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    class="text-lg-left"
                    :items="patient_case.OprOrderDetails"
                    :fields="OprOrderDetails_fields"
                    bordered
                  ></b-table>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="OprOrderDetails"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.OprOrderDetails_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea v-model="case_review.OprOrderDetails">
                        </b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row
                class="mb-5 border py-2 card-shadow"
                v-if="patient_case.medical_type == 2"
              >
                <!-- <b-col md="8">
                <h6 class="mb-1">Attending Notes</h6>
                <b-table responsive class="text-lg-left" bordered :items="patient_case.attending_notes"
                  :fields="attending_fields"></b-table>
              </b-col> -->
                <b-col md="8">
                  <h6 class="mb-1">Attending Notes</h6>
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('attending_notes')"
                    :error="getSectionError('attending_notes')"
                    title="Failed to load Attending Notes"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    responsive
                    class="text-lg-left"
                    bordered
                    :items="patient_case.attending_notes"
                    :fields="attending_fields"
                  >
                    <template #cell(description)="row">
                      {{ row.value }}
                    </template>
                  </b-table>
                </b-col>
                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="AttendingNote"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.attending_note_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>

                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Comment</h6>
                        <b-form-textarea
                          v-model="case_review.attending_note"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row
                class="mb-5 border py-2 card-shadow"
                v-if="patient_case.medical_type == 2"
              >
                <b-col md="8">
                  <h6 class="mb-1">Investigation</h6>
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('investigation')"
                    :error="getSectionError('investigation')"
                    title="Failed to load Investigation"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    :items="patient_case.investigation"
                    class="text-lg-left"
                    bordered
                    :fields="investigation_fields"
                  ></b-table>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="investigations"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.investigations_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>

                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Comment</h6>
                        <b-form-textarea
                          v-model="case_review.investigations"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row class="mb-5 border py-2 card-shadow">
                <b-col md="8">
                  <h6 class="mb-1">Diagnosis</h6>
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('diagnosis')"
                    :error="getSectionError('diagnosis')"
                    title="Failed to load Diagnosis"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    :items="patient_case.diagnosis"
                    class="text-lg-left"
                    :fields="diagnosis_fields"
                    bordered
                  ></b-table>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="Diagnosis"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.diagnosis_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.diagnosis"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row class="mb-5 border py-2 card-shadow">
                <b-col md="8">
                  <h6 class="mb-1">Chief Complaint</h6>
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('chief_complaint')"
                    :error="getSectionError('chief_complaint')"
                    title="Failed to load Chief Complaint"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    class="text-lg-left"
                    :items="patient_case.chief_complaint"
                    :fields="chief_fields"
                    bordered
                  >
                    <template #cell(description)="data">
                      <p
                        v-if="
                          data.item.description && data.item.description[0].Data
                        "
                      >
                        {{ data.item.description[0].Data }}
                      </p>
                      <p v-else>{{ data.item.description }}</p>
                    </template>
                    <!-- <template #cell(description)="data">
                    <p
                      v-if="
                        data.item.description && data.item.description[0].Data
                      "
                    >
                      {{ data.item.description[0].Data }}
                    </p>
                    <p v-else>{{ data.item.description }}</p>
                  </template> -->
                  </b-table>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="chiefComplaint"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.chief_complaint_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>

                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.chief_complaint"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col md="8">
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('ChfTreatmentplan')"
                    :error="getSectionError('ChfTreatmentplan')"
                    title="Failed to load Chief Treatment Plan"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    class="text-lg-left"
                    :items="patient_case.ChfTreatmentplan"
                    :fields="chief_treat_fields"
                    bordered
                  >
                    <template #cell(description)="data">
                      <p
                        v-if="
                          data.item.description && data.item.description[0].Data
                        "
                      >
                        {{ data.item.description[0].Data }}
                      </p>
                      <p v-else>{{ data.item.description }}</p>
                    </template>
                    <!-- <template #cell(description)="data">
                    <p
                      v-if="
                        data.item.description && data.item.description[0].Data
                      "
                    >
                      {{ data.item.description[0].Data }}
                    </p>
                    <p v-else>{{ data.item.description }}</p>
                  </template> -->
                  </b-table>
                </b-col>
              </b-row>
              <b-row class="mb-5 border py-2 card-shadow">
                <b-col md="8">
                  <h6 class="mb-1">History</h6>
                  <b-table
                    class="text-lg-left"
                    :items="patient_case.chief_complaint"
                    :fields="chief_history_fields"
                    bordered
                  >
                  </b-table>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="chiefComplaint"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.chief_history_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>

                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.chief_history"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <b-row class="mb-5 border py-2 card-shadow"> </b-row>

              <b-row class="mb-5 border py-2 card-shadow">
                <b-col md="8">
                  <h5 class="mb-1">Vital Signs</h5>

                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="getSectionError('vital_signs')"
                    :error="getSectionError('vital_signs')"
                    title="Failed to load Vital Signs"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    bordered
                    :items="patient_case.vital_signs"
                    :fields="vitalfields"
                  ></b-table>
                </b-col>

                <b-col md="4">
                  <b-row>
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="vitalSigns"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.vital_signs_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <h6>Add Custom Comment</h6>

                      <b-form-textarea v-model="case_review.vital_signs">
                      </b-form-textarea>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <b-row class="">
                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Consent Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="Consent"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.consent_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.consent"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>

                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Discharge Summary Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="Discharge"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.discharge_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.discharge"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col md="4">
                  <b-row class="mb-2">
                    <b-col md="12">
                      <h6 class="mb-1">Operative Report Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="operativeReport"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.operative_report_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>
                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <b-form-group>
                        <h6>Add Custom Comment</h6>
                        <b-form-textarea
                          v-model="case_review.operative_report"
                        ></b-form-textarea>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="12" class="mt-2">
                  <b-form-group>
                    <h6>Notes</h6>
                    <b-form-textarea
                      v-model="case_review.notes"
                    ></b-form-textarea>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="mb-2 mt-2">
                <b-col md="4"> </b-col>
                <b-col md="3">
                  <h6 class="mb-1">Type status</h6>
                  <b-form-group>
                    <validation-provider
                      #default="{ errors }"
                      name="AuditTypeStatus"
                      rules="required"
                    >
                      <v-select
                        :options="type_status"
                        v-model="case_review.type_status"
                        :reduce="(val) => val.id"
                      >
                        <template v-slot:option="option">
                          {{ option.type }}
                        </template>
                        <template #selected-option="{ type }">
                          {{ type }}
                        </template>
                        <template #no-options>
                          {{ $t("noMatching") }}
                        </template>
                      </v-select>
                      <ValidationErrors :frontend-errors="errors" />
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </validation-observer>
          </b-tab>

          <b-tab>
            <template #title>
              <feather-icon icon="CheckSquareIcon" />
              <span> Case History</span>
            </template>

            <validation-observer ref="caseHistoryObserver">
              <b-card title="History">
                <b-timeline
                  :items="timelineItems"
                  :reverse="false"
                  date-format="dd/MM/yy HH:mm:ss"
                  variant="primary"
                  :human-friendly-time="true"
                />
              </b-card>
            </validation-observer>
          </b-tab>
          <b-tab>
            <template #title>
              <feather-icon icon="CheckSquareIcon" />
              <span> Approval Tracking </span>
            </template>
            <validation-observer ref="approvalTrackingObserver">
              <b-row>
                <b-col md="12">
                  <strong class="ml-2 mb-2"> Case Approvals</strong>
                  <!-- Show error if section failed -->
                  <SectionError
                    v-if="
                      getSectionError('approvals') ||
                      getSectionError('approvals_auto') ||
                      getSectionError('approvals_manual')
                    "
                    :error="
                      getSectionError('approvals') ||
                      getSectionError('approvals_auto') ||
                      getSectionError('approvals_manual')
                    "
                    title="Failed to load Case Approvals"
                  />
                  <!-- Show table if no error -->
                  <b-table
                    v-else
                    class="mt-1"
                    :items="patient_case.approvals"
                    :fields="case_approvals_fields"
                    striped
                  >
                    <template #cell(date)="data">
                      {{ data.item.date }}
                    </template>
                  </b-table>
                </b-col>
                <!-- <b-col md="3"></b-col> -->
                <b-col md="3">
                  <b-row>
                    <b-col md="12">
                      <h6 class="mb-1">Review status</h6>
                      <b-form-group>
                        <validation-provider
                          #default="{ errors }"
                          name="ApprovalsStatus"
                          rules="required"
                        >
                          <v-select
                            :options="review_status"
                            v-model="case_review.approvals_type"
                            :reduce="(val) => val.id"
                          >
                            <template v-slot:option="option">
                              {{ option.type }}
                            </template>
                            <template #selected-option="{ type }">
                              {{ type }}
                            </template>
                            <template #no-options>
                              {{ $t("noMatching") }}
                            </template>
                          </v-select>

                          <ValidationErrors :frontend-errors="errors" />
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col md="12">
                      <h6>Add Custom Comment</h6>
                      <b-form-textarea v-model="case_review.approvals">
                      </b-form-textarea>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </validation-observer>
          </b-tab>

          <div v-if="hasPermission('access_collected-data')">
            <b-tab>
              <!-- <validation-observer ref="medicalRecordObserver"> -->
              <template #title>
                <feather-icon icon="CheckSquareIcon" />
                <span>Medical Record</span>
              </template>
              <!-- </validation-observer> -->
              <keep-alive>
                <MedicalRecord
                  @medical_reviews_data="handleDataUpdate"
                  :medicalReviewsData="medical_reviews"
                />
              </keep-alive>
            </b-tab>
          </div>
        </template>
      </show>
    </b-card>
    <b-row class="mb-5">
      <b-col md="12" class="d-flex justify-content-between">
        <div v-if="status == 0 || status == 2">
          <b-button class="btn btn-info mr-2" @click="SaveReview">
            Save
          </b-button>
          <b-button class="btn btn-info mr-2" @click="SaveMedicalReview">
            Save Medical Review
          </b-button>
          <router-link :to="`/cases`">
            <b-button class="mr-5"> Cancel </b-button>
          </router-link>
        </div>

        <div v-if="status == 0 || status == 2">
          <b-button class="btn btn-danger mr-2" @click="handleStatusChange(1)">
            Return to Branch
          </b-button>
          <b-button class="btn btn-success mr-2" @click="handleStatusChange(3)">
            Close Case
          </b-button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import vSelect from "vue-select";
import show from "@/views/cases/components/show";
import {
  BTab,
  BCardText,
  BCard,
  BBadge,
  BCol,
  BTextArea,
  BRow,
  BTable,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFromRadioGroup,
  BFormRadio,
  BFormTextarea,
  BSpinner,
  BAlert,
  BCollapse,
  VBTooltip,
} from "bootstrap-vue";
import List from "@/views/components/info/list";
import BootstrapVueTimeline from "bootstrap-vue-timeline";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import { required, email } from "@validations";
import ValidationErrors from "@/views/components/common/ValidationErrors";
import MedicalRecord from "@/views/cases/components/MedicalRecord.vue";
import SectionError from "@/views/cases/components/SectionError.vue";

Vue.component("b-timeline", BootstrapVueTimeline);

export default {
  name: "patient_case",
  components: {
    MedicalRecord,
    BootstrapVueTimeline,
    vSelect,
    show,
    BFormCheckbox,
    BFormCheckboxGroup,
    BTab,
    BFormTextarea,
    BTextArea,
    BCardText,
    BCard,
    BCol,
    BRow,
    List,
    BBadge,
    BTable,
    BForm,
    BFormGroup,
    BFromRadioGroup,
    BFormInput,
    BFormRadio,
    BButton,
    ValidationErrors,
    ValidationProvider,
    ValidationObserver,
    localize,
    BSpinner,
    BAlert,
    BCollapse,
    SectionError,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      loading: false,
      error: null,
      showErrorDetails: false,
      expandedErrors: [],
      case_approvals_fields: [
        {
          key: "approval_type",
          label: "Apprroval Type ",
        },
        {
          key: "service_code",
          label: "Service Code",
        },
        {
          key: "service_name",
          label: "Service Name",
        },
        {
          key: "total_qty_service",
          label: "Total Quantity Service",
        },
        {
          key: "is_approved",
          label: "Status ",
        },
        {
          key: "approval_number",
          label: "Approval Number",
        },
        {
          key: "manual.name",
          label: " Manual / Auto",
        },

        {
          key: "insurance_membership_no",
          label: "Membership ID ",
        },

        {
          key: "approval_date",
          label: " Approval Date",
        },

        {
          key: "account_no",
          label: "Account Number",
        },
      ],
      investigation_fields: [
        {
          key: "tooth_no",
          label: "Tooth No.",
        },
        {
          key: "service_name",
          label: "Name",
        },
        {
          key: "service_code",
          label: "Code",
        },

        {
          key: "visit_no",
          label: "Visit Number",
        },
        {
          key: "date",
          label: "Date",
        },

        {
          key: "dr_name",
          label: "Doctor",
        },
        {
          key: "total_qty_service",
          label: "Total Quantity Service",
        },
      ],
      charge_items_fields: [
        {
          key: "name",
          label: "Name",
        },
        {
          key: "code",
          label: "Code",
        },
        {
          key: "serial_no",
          label: "Account no ",
        },
        // {
        //   key: "financial_category",
        //   label: "financial category ",
        // },
        {
          key: "dr_name",
          label: "doctor",
        },
        {
          key: "admission_date",
          label: "admission date",
        },
        {
          key: "final_bill_date",
          label: "Discharge Date",
        },
      ],

      diagnosis_fields: [
        {
          key: "description",
          label: "Description ",
        },
        {
          key: "code",
          label: "Code",
        },

        {
          key: "visit_no",
          label: " Visit Number ",
        },
        {
          key: "diagnosis_date",
          label: "Date",
        },
        {
          key: "dr_name",
          label: "Doctor",
        },
      ],
      vitalfields: [
        {
          key: "visit_no",
          label: " Visit Number ",
        },
        {
          key: "vital_date",
          label: "Date ",
        },
        {
          key: "temperature",
          label: "Temprature",
        },
        {
          key: "pulse",
          label: "Pulse",
        },
        {
          key: "systole",
          label: "Systole",
        },
        {
          key: "diastole",
          label: "Diastole",
        },
        {
          key: "weight",
          label: "Weight",
        },
      ],
      chief_fields: [
        {
          key: "description",
          label: "Description ",
        },

        {
          key: "visit_no",
          label: " Visit Number ",
        },
        {
          key: "chief_date",
          label: "Date",
        },
        {
          key: "user_name",
          label: "User",
        },
      ],
      chief_history_fields: [
        {
          key: "visit_no",
          label: " Visit Number ",
        },
        {
          key: "chief_date",
          label: "Date",
        },
        {
          key: "user_name",
          label: "User",
        },
        {
          key: "code",
          label: "history  ",
        },
        {
          key: "family",
          label: "family  ",
        },
        {
          key: "present",
          label: "present  ",
        },
      ],
      chief_treat_fields: [
        {
          key: "abbreviation",
          label: "abbreviation ",
        },
        {
          key: "code",
          label: "Code",
        },

        {
          key: "eye_code",
          label: " Eye code  ",
        },
        {
          key: "date",
          label: "Date",
        },
        {
          key: "dr_name",
          label: "Doctor",
        },
      ],
      attending_fields: [
        {
          key: "visit_no",
          label: "Visit Number ",
        },
        {
          key: "dr_name",
          label: "Doctor ",
        },
        {
          key: "description",
          label: "Description",
        },
        // {
        //   key: "time",
        //   label: "Time",
        // },
        {
          key: "att_date",
          label: "Date",
        },
      ],

      surgeries_fields: [
        {
          key: "area",
          label: "Area ",
        },
        {
          key: "abbreviation",
          label: "Abbreviation ",
        },
        // {
        //   key: "code",
        //   label: "Code ",
        // },
        {
          key: "side",
          label: "Side ",
        },
        {
          key: "date",
          label: "Surgery Date",
        },
        {
          key: "code",
          label: "code",
        },
        {
          key: "dr_name",
          label: "Doctor Name",
        },
        {
          key: "surgeon_name",
          label: "Surgery Name",
        },
      ],

      IfaTreatmentplan_fields: [
        {
          key: "code",
          label: "code",
        },
        {
          key: "area",
          label: "Area ",
        },
        {
          key: "abbreviation",
          label: "Abbreviation ",
        },
        {
          key: "AreaCode",
          label: "Area Code ",
        },
        {
          key: "AreaDesc",
          label: "Area Desc ",
        },
        {
          key: "Loc1Code",
          label: "Side Code ",
        },
        // {
        //   key: "Loc1Desc",
        //   label: "Lock Desc ",
        // },
        {
          key: "side",
          label: "Side ",
        },
        {
          key: "date",
          label: "Date",
        },
      ],
      operative_procedures_fields: [
        {
          key: "opr_code",
          label: "Code",
        },
        {
          key: "position_code",
          label: "position code ",
        },
        {
          key: "opr_status",
          label: "status  ",
        },
        {
          key: "position_desc",
          label: "Side ",
        },
        {
          key: "opr_date",
          label: "Date",
        },
        {
          key: "dr_name",
          label: "Doctor Name",
        },
      ],
      ended_operative_procedures_fields: [
        {
          key: "fin_code",
          label: "Fin Code",
        },
        {
          key: "specialty",
          label: "specialty ",
        },
        /* {
          key: "opr_status",
          label: "status  ",
        },*/
        {
          key: "abbreviation",
          label: "abbreviation ",
        },
        {
          key: "service_code",
          label: "service code",
        },
        {
          key: "visit_no",
          label: "Account no",
        },
        {
          key: "financial_category",
          label: "financial category",
        },
        {
          key: "dr_name",
          label: "Doctor Name",
        },
        {
          key: "final_bill_date",
          label: "Date   ",
        },
      ],
      OprOrderDetails_fields: [
        {
          key: "ord_cat",
          label: "ord type ",
        },
        {
          key: "ord_dep",
          label: "ord dep ",
        },
        {
          key: "position",
          label: "position",
        },
        {
          key: "ord_service_code",
          label: "service code ",
        },
        {
          key: "ord_caseno",
          label: "case no  ",
        },
        {
          key: "qty",
          label: "Qty  ",
        },
        {
          key: "ord_comment",
          label: "comment ",
        },
        {
          key: "dr_name",
          label: "Doctor Name  ",
        },
        {
          key: "ord_date",
          label: "Date  ",
        },
      ],
      invoice_fields: [
        {
          key: "serial_no",
          label: "Serial Number",
        },
        {
          key: "financial_category",
          label: "Financial Category",
        },
        {
          key: "date",
          label: "date",
        },
      ],

      planfields: [
        /* {
          key: "area",
          label: "Area ",
        },*/
        {
          key: "abbreviation",
          label: "Abbreviation ",
        },
        {
          key: "side_name",
          label: "Side  ",
        },
        {
          key: "dr_name",
          label: "Doctor ",
        },
        {
          key: "side_code",
          label: "Position  ",
        },
        {
          key: "code",
          label: "Code  ",
        },
        {
          key: "plan_date",
          label: "Date  ",
        },
      ],
      review_status: [
        {
          id: 0,
          type: "Compliance",
        },
        {
          id: 1,
          type: " Non-Compliance",
        },
        {
          id: 2,
          type: " Missed",
        },
      ],
      type_status: [
        {
          id: 0,
          type: "Serious",
        },
        {
          id: 1,
          type: "Non-serious",
        },
      ],
      // medical_reviews: {},
      medical_reviews: {
        bbp_consent: null,
        bbp_status: null,

        hrp_consent: null,
        hrp_status: null,

        hpi: null,
        hpi_status: null,

        sys_history: null,
        sys_history_status: null,

        pmh: null,
        pmh_status: null,

        allergy: null,
        allergy_status: null,

        fam_history: null,
        fam_history_status: null,

        eye_exam: null,
        eye_exam_status: null,

        ref_consult: null,
        ref_consult_status: null,

        ips4: null,
        ips4_status: null,

        anes_consent: null,
        anes_consent_status: null,

        sedation_sheet: null,
        sedation_sheet_status: null,

        pre_anes_assessment: null,
        pre_anes_assessment_status: null,

        preop_assessment: null,
        preop_assessment_status: null,

        arr_discharge_time: null,
        arr_discharge_time_status: null,

        nursing_assessment: null,
        nursing_assessment_status: null,

        ips2: null,
        ips2_status: null,

        nutr_screen: null,
        nutr_screen_status: null,

        func_screen: null,
        func_screen_status: null,

        psycho_screen: null,
        psycho_screen_status: null,

        early_discharge_plan: null,
        early_discharge_plan_status: null,

        pain_assessment: null,
        pain_assessment_status: null,

        pat_edu: null,
        pat_edu_status: null,

        fall_assessment: null,
        fall_assessment_status: null,

        postop_fall_assessment: null,
        postop_fall_assessment_status: null,

        fall_prevention: null,
        fall_prevention_status: null,

        med_order: null,
        med_order_status: null,

        nutr_assessment: null,
        nutr_assessment_status: null,

        sw_assessment: null,
        sw_assessment_status: null,

        type_status: null,
      },
      case_review: {
        charge_items: null,
        charge_items_type: null,
        treatment_plan: null,
        treatment_plan_type: null,
        operative_procedure: null,
        operative_procedure_type: null,
        ifa_operative_procedure_type: null,
        ifa2_operative_procedure_type: null,
        OprOrderDetails_type: null,
        attending_note: null,
        attending_note_type: null,
        investigations: null,
        investigations_type: null,
        diagnosis: null,
        diagnosis_type: null,
        dr_name: null,
        dr_name_type: null,
        chief_complaint: null,
        chief_complaint_type: null,
        consent: null,
        consent_type: null,
        discharge: null,
        discharge_type: null,
        operative_report: null,
        operative_report_type: null,
        notes: null,
        type_status: null,
        approvals_type: null,
        approvals: null,
      },
      timelineItmems: [
        {
          timestamp: Date.parse("2021-05-28T20:20:46.444Z"),
          title: "Dolore ullamco exercitation commodo",
          content: "Esse dolore consectetur aliqua laboris sunt aliqua do non.",
        },
        {
          timestamp: Date.parse("2021-05-28T20:20:46.444Z"),
          title: "Voluptate pariatur dolore laborum eu",
        },
      ],
      requiredFields: [
        "charge_items_type",
        "treatment_plan_type",
        "ifa_treatment_plan_type",
        "operative_procedure_type",

        "diagnosis_type",
        "chief_complaint_type",
        "approvals_type",
        "type_status",

        "consent_type",
        "discharge_type",
        "operative_report_type",
      ],
    };
  },
  watch: {
    case_review: {
      handler(newVal) {
        console.log("case_review updated:", newVal);
      },
      deep: true,
    },
  },
  methods: {
    print() {
      console.log(this.case_review.consent);
    },
    move(tabIndex) {
      console.log(tabIndex);
    },
    handleDataUpdate(updatedData) {
      // Do something with the updated data in the parent component
      console.log("Received updated data:", updatedData);

      // You can also store it in a data property or perform other actions
      this.medical_reviews = updatedData;
    },

    init() {
      // Clear any previous error
      this.error = null;

      this.$store
        .dispatch("cases/get", this.id)
        .then((response) => {
          if (response.data.MedicalReview !== null) {
            this.medical_reviews = response.data.MedicalReview;
          }
          if (response.data.case_review !== null) {
            this.case_review = response.data.case_review;
            if (this.case_review.consent == 1) {
              this.case_review.consent = true;
            }

            if (this.case_review.discharge == 1) {
              this.case_review.discharge = true;
            }

            if (this.case_review.operative_report == 1) {
              this.case_review.operative_report = true;
            }
          }
        })
        .catch((error) => {
          console.error("Failed to load case:", error);

          // Handle different error scenarios
          if (error.response) {
            // Server responded with error
            if (error.response.status === 404) {
              this.error =
                "Case not found. It may have been deleted or the ID is invalid.";
            } else if (error.response.status === 403) {
              this.error = "You do not have permission to view this case.";
            } else if (error.response.status === 500) {
              this.error = "Server error. Please try again later.";
            } else {
              this.error =
                error.response.data?.message ||
                "An error occurred while loading the case.";
            }
          } else if (error.request) {
            // Network error
            this.error =
              "Network error. Please check your connection and try again.";
          } else {
            // Other error
            this.error = "An unexpected error occurred. Please try again.";
          }
        });
    },

    // Format section name from snake_case to Title Case
    formatSectionName(section) {
      if (!section) return "";
      return section
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    // Toggle visibility of technical error details
    toggleTechnicalError(index) {
      const idx = this.expandedErrors.indexOf(index);
      if (idx === -1) {
        this.expandedErrors.push(index);
      } else {
        this.expandedErrors.splice(idx, 1);
      }
    },

    // Get error for a specific section
    getSectionError(sectionName) {
      if (!this.patient_case || !this.patient_case.errors) {
        return null;
      }
      return (
        this.patient_case.errors.find((err) => err.section === sectionName) ||
        null
      );
    },

    // Check if section has error
    hasSectionError(sectionName) {
      return this.getSectionError(sectionName) !== null;
    },

    checkRequiredFields(requiredFields = this.requiredFields) {
      // First, let's log the current state to debug
      console.log("Current case_review state:", this.case_review);

      const missingFields = requiredFields.filter((field) => {
        const value = this.case_review[field];
        // Check if the value is null, undefined, or empty string
        return value === null || value === undefined || value === "";
      });

      if (missingFields.length > 0) {
        // Log the missing fields for debugging
        console.log("Missing fields:", missingFields);

        this.showError(
          "Missing Required Fields",
          `Please fill in the following fields:<br><br>${missingFields
            .map((field) => field.replace(/_/g, " ").toUpperCase())
            .join("<br>")}`,
        );
        return false;
      }
      return true;
    },
    // Handles status change
    async handleStatusChange(status) {
      if (this.loading) return;
      this.loading = true;
      try {
        if (!this.checkRequiredFields() || !(await this.validateForm())) return;
        await this.SaveReview();
        await this.changeStatus(status);
        this.showSuccess("Status Changed Successfully");
        this.init();
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.loading = false;
      }
    },

    // Changes the status
    async changeStatus(status) {
      try {
        // Ensure required fields are filled before proceeding
        const requiredFields = [
          "charge_items_type",
          "treatment_plan_type",
          "ifa_treatment_plan_type",
          "operative_procedure_type",
          "diagnosis_type",
          "chief_complaint_type",
          "approvals_type",
          "type_status",

          "consent_type",
          "discharge_type",
          "operative_report_type",
          "approvals_type",
        ];

        if (!this.checkRequiredFields(requiredFields)) return;

        // Validate the form
        if (!(await this.validateForm())) return;

        // Dispatch the status change action
        await this.$store.dispatch("cases/changeStatus", {
          id: this.id,
          query: { status },
        });

        this.showSuccess("Status Changed Successfully");
        this.init();
      } catch (error) {
        console.error("Error during status change:", error);

        // Handle different types of errors
        let errorMessage = "An error occurred while changing the status.";

        if (error.response?.data?.message) {
          if (
            error.response.data.message.includes("Lock wait timeout exceeded")
          ) {
            errorMessage =
              "Database is currently busy. Please try again in a few moments.";
          } else {
            errorMessage = error.response.data.message;
          }
        }

        this.$swal({
          icon: "error",
          title: "Status Change Failed",
          text: errorMessage,
          confirmButtonText: "OK",
        });
      }
    },

    // Saves the review
    async SaveReview() {
      try {
        // Required fields for saving the review
        const requiredFields = [
          "charge_items_type",
          "treatment_plan_type",
          "ifa_treatment_plan_type",
          "operative_procedure_type",
          "diagnosis_type",
          "chief_complaint_type",
          "approvals_type",
          "type_status",

          "consent_type",
          "discharge_type",
          "operative_report_type",
          "approvals_type",
        ];

        if (!this.checkRequiredFields(requiredFields)) return;

        this.case_review.case_id = this.id;

        // Dispatch save action
        await this.$store.dispatch("case_review/put", {
          id: this.review_id,
          query: this.case_review,
        });

        this.showSuccess(this.$t("Global.Saved"));
      } catch (error) {
        console.error("Error during save:", error);
        this.handleSaveError(error);
      }
    },

    // Validates the form
    async validateForm() {
      try {
        const refsToValidate = [
          this.$refs.auditViewObserver,
          this.$refs.caseHistoryObserver,
          this.$refs.approvalTrackingObserver,
          this.$refs.medicalObserver,
          this.$refs.medicalRecordObserver,
        ].filter(Boolean);

        // if (refsToValidate.length === 0) {
        //   console.warn("No validation references found");
        //   return false;
        // }

        const validationResults = await Promise.all(
          refsToValidate.map((ref) =>
            ref.validate().then((isValid) => ({ isValid, errors: ref.errors })),
          ),
        );

        const allErrors = validationResults.reduce((errors, result) => {
          if (!result.isValid) {
            Object.assign(errors, result.errors);
          }
          return errors;
        }, {});

        if (Object.keys(allErrors).length > 0) {
          const errorMessages = Object.entries(allErrors)
            .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
            .join("<br>");
          this.showError("Validation Errors", errorMessages);
          return false;
        }

        return true;
      } catch (error) {
        console.error("Validation error:", error);
        this.showError(
          "Validation Error",
          "An error occurred during validation.",
        );
        return false;
      }
    },

    // Handles save errors
    handleSaveError(error) {
      let errorMessage = "An error occurred while saving. Please try again.";
      if (error.response?.data?.errors) {
        errorMessage = Object.entries(error.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
          .join("<br>");
      }
      this.showError("Save Failed", errorMessage);
    },

    // Utility to show success messages
    showSuccess(message) {
      this.$swal({
        icon: "success",
        title: message,
        showConfirmButton: false,
        timer: 1500,
      });
    },

    // Utility to show error messages
    showError(title, message) {
      this.$swal({
        icon: "error",
        title,
        html: message,
        confirmButtonText: "OK",
      });
    },

    SaveMedicalReview() {
      this.medical_reviews.case_id = this.id;
      console.log("hhhhhh", this.medical_reviews);
      this.$store
        .dispatch("medical_review/put", {
          id: this.medical_review_id,
          query: this.medical_reviews,
        })
        .then((_) => {
          this.$swal({
            icon: "success",
            title: this.$t("Global.Saved"),
            showConfirmButton: false,
            timer: 1500,
          });
        })
        .catch((error) => {
          this.errorsdata = this.handleBackendError(error.response.data.errors);
        });
    },
  },

  computed: {
    // timelineItems() {
    //   let timeline = [];
    //   let auditor_name = "";
    //   if (this.patient_case.auditor) {
    //     auditor_name = this.patient_case.auditor.name;
    //   }
    //   // Extract user from patient_case.description
    //   if (this.patient_case.description) {
    //     try {
    //       const parsedDescription = JSON.parse(this.patient_case.description);
    //       user =
    //         parsedDescription.length > 0 ? parsedDescription[0].User : "cccccc";
    //     } catch (error) {
    //       console.error("Error parsing description:", error);
    //     }
    //   }
    //   console.log("usss",this.patient_case.history);
    //   this.patient_case.history.forEach((element) =>
    //     timeline.push({
    //       title: element.title,
    //       timestamp: Date.parse(element.timestamp),
    //       content: "User: " + this.authUser().first_name,
    //     })
    //   );
    //   return timeline;
    // },

    timelineItems() {
      let timeline = [];

      if (this.patient_case.history && this.patient_case.history.length > 0) {
        this.patient_case.history.forEach((element) => {
          let userName;

          // لو user = null → Admin
          if (!element.user || !element.user.name) {
            userName = "Admin";
          } else {
            userName = element.user.name;
          }

          timeline.push({
            title: element.title,
            timestamp: Date.parse(element.timestamp),
            content: "User: " + userName,
          });
        });
      } else {
        timeline.push({
          title: "Case Created",
          timestamp: Date.now(),
          content: "User: Admin",
        });
      }

      return timeline;
    },

    ...mapGetters({
      patient_case: "cases/item",
      load: "cases/load",
    }),

    sectionErrors() {
      if (this.patient_case && this.patient_case.errors) {
        return this.patient_case.errors;
      }
      return [];
    },

    status() {
      if (this.patient_case && this.patient_case.status) {
        return this.patient_case.status.status;
      } else {
        return 0;
      }
    },
    medical_review_id() {
      console.log("a7a", this.$store.getters["cases/item"]);
      if (this.$store.getters["cases/item"].MedicalReview !== null) {
        return this.$store.getters["cases/item"].MedicalReview.id;
      } else {
        return null;
      }
    },
    review_id() {
      if (this.$store.getters["cases/item"].case_review !== null) {
        return this.$store.getters["cases/item"].case_review.id;
      } else {
        return null;
      }
    },
    id() {
      return this.$route.params.id ? this.$route.params.id : null;
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped>
.card-shadow {
  box-shadow: 1px 1px 1px #eee, 1px 1px 1px #e0dcdc;
}

.size-min {
  font-size: 10px;
}

.custom_last_table tbody tr th {
  width: 20%;
}
</style>
